import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  Node,
  Edge,
  ConnectionMode,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
} from '@xyflow/react';
import { GRID_CONFIG } from '../gridConfig';
import { getTotalGridWidth, getTotalGridHeight } from '../utils/layoutMath';
import { createMapNode } from './Map3Relatives';
import {
  createResultNodesFromRelativesMapData,
  createEdgesFromDependencies,
} from '../utils/reactFlowUtils';
import { useContainerResize } from '../hooks/useContainerResize';
import { useKeyboardHandler } from '../hooks/useKeyboardHandler';
import { useConnectHandler } from '../hooks/useConnectHandler';
import { useEdgesChangeHandler } from '../hooks/useEdgesChangeHandler';
import { useDependencyHandler } from '../hooks/useDependencyHandler';
import { useNodesChangeHandler } from '../hooks/useNodesChangeHandler';
import { ReactFlow } from '@xyflow/react';
import { Map3RelativesBaseProps } from './Map3Relatives';

import { ResultCircleNodeData, circleNodeTypes } from './ResultCircleNode';
import {
  DraggableResultCircleNodeData,
  draggableCircleNodeTypes,
} from './DraggableResultCircleNode';
import { mapNodeTypes } from './MapNode';
import './Map3Relatives.css';
import { FullyCollapsedMapsGroup } from './FullyCollapsedMapsGroup';

const nodeTypes = {
  ...circleNodeTypes,
  ...draggableCircleNodeTypes,
  ...mapNodeTypes,
};

export function Map3RelativesStacked({
  mainMilestoneMapUuid,
  relatedMilestoneMaps,
  allObjectives,
  dependencyRecords,
  children,
  queueResultDeadlineUpdate = () => {},
  queueDependencyAdd = () => {},
  queueDependencyRemove = () => {},
  settings = {},
  objectiveDisplayMode = 'show_all',
  relativesMapsData,
  updateMapState,
  canEdit,
}: Map3RelativesBaseProps) {
  // Check if result circles are enabled for the main map
  const mainMilestoneMap = relatedMilestoneMaps.find((m) => m.uuid === mainMilestoneMapUuid);

  // Get display settings from settings (passed from wrapper which gets it from Redux)
  // Note: This doesn't actually work right now. Getting these background colors to update
  // will take a larger refactor.  For now, updates to Map3Controls here 37d6e958870ea5fffdbf2b5661a6883eac2aa704
  // ensure the UI and controls stay in sync.
  const toggleStatusCellDisplay = settings.toggleStatusCellDisplay ?? 'status_results2_comments';
  const showResultCircles = toggleStatusCellDisplay !== 'status';
  const enableResultCircleFill = toggleStatusCellDisplay === 'status_results2_comments';
  const showStatusCodes = settings.showStatusCodes ?? false;

  // Drag and connect mode state management
  const [dragAndConnectModeResultUuid, setDragAndConnectModeResultUuid] = useState<string | null>(
    null,
  );

  const handleEnterDragAndConnectMode = useCallback(
    (resultUuid: string) => {
      if (!canEdit) {
        console.warn(
          '[Map3RelativesStacked] Drag and connect mode denied: user does not have edit permissions',
        );
        return;
      }
      console.log('[Map3RelativesStacked] Entering drag and connect mode for result:', resultUuid);
      setDragAndConnectModeResultUuid(resultUuid);
    },
    [canEdit],
  );

  const handleExitDragAndConnectMode = useCallback((resultUuid: string) => {
    console.log('[Map3RelativesStacked] Exiting drag and connect mode for result:', resultUuid);
    setDragAndConnectModeResultUuid(null);
  }, []);

  // Create a layout key that captures when the layout structure changes
  const layoutKey = useMemo(() => {
    return relativesMapsData
      .map((m) => `${m.milestoneMap.uuid}:${m.objectives.map((o) => o.uuid).join(',')}`)
      .join('||');
  }, [relativesMapsData]);

  // State Management
  const flowWrapper = useRef<HTMLDivElement>(null);
  const dimensions = useContainerResize(flowWrapper);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedEdges, setSelectedEdges] = useState<Edge[]>([]);

  // Hover state for thick lines
  const [hoveredThickLineUuid, setHoveredThickLineUuid] = useState<string | null>(null);

  const { addDependency, removeDependency, removeDependenciesByIds } = useDependencyHandler(
    dependencyRecords,
    queueDependencyAdd,
    queueDependencyRemove,
    canEdit,
  );

  // The main map will be displayed at the top. We will find and
  // display all dependencies for the main map.
  // Note: Validation moved after all hooks to comply with rules of hooks.
  // Check is performed before the return statement.
  const mainMap = relatedMilestoneMaps.find((m) => m.uuid === mainMilestoneMapUuid);

  // Use the passed relativesMapsData instead of computing it
  // (relativesMapsData is now computed once in the wrapper component)

  // Generate nodes/edges.
  useEffect(() => {
    // Create MapNodes and their child result nodes using sub-flows pattern
    const allNodes: Node[] = [];

    // First pass: collect fullyCollapsed maps for thick line rendering
    const fullyCollapsedMaps = relativesMapsData.filter(
      (relative) => relative.collapseLevel === 'fullyCollapsed',
    );

    relativesMapsData.forEach((relative, i) => {
      if (relative.collapseLevel === 'fullyCollapsed') {
        // For fullyCollapsed maps, only create result nodes for connections
        // The thick lines are rendered externally via FullyCollapsedRendering component
        const fullyCollapsedIndex = fullyCollapsedMaps.findIndex(
          (collapsed) => collapsed.milestoneMap.uuid === relative.milestoneMap.uuid,
        );

        const resultNodes = createResultNodesFromRelativesMapData(
          relative,
          enableResultCircleFill,
          dragAndConnectModeResultUuid,
          handleEnterDragAndConnectMode,
          handleExitDragAndConnectMode,
          showStatusCodes,
          fullyCollapsedIndex,
          fullyCollapsedMaps.length,
        );
        allNodes.push(...resultNodes);
      } else {
        // Create regular MapNode (parent container) for expanded/titleOnly maps
        const mapNode = createMapNode(
          relative,
          settings,
          relative.milestoneMap.interval,
          updateMapState,
          mainMilestoneMapUuid,
        );
        allNodes.push(mapNode);

        // Create result nodes (always circle nodes now)
        const resultNodes = createResultNodesFromRelativesMapData(
          relative,
          enableResultCircleFill,
          dragAndConnectModeResultUuid,
          handleEnterDragAndConnectMode,
          handleExitDragAndConnectMode,
          showStatusCodes,
          0, // fullyCollapsedIndex (not used for expanded/titleOnly)
          1, // totalFullyCollapsedMaps (not used for expanded/titleOnly)
        );
        allNodes.push(...resultNodes);
      }
    });

    // Reset node positions if objective display mode or layout changed, otherwise preserve positions for smooth transitions
    setNodes((prevNodes) => {
      // Check if layout structure or display mode changed
      const prevObjectiveDisplayMode = (
        prevNodes.find(
          (n) => n.type === 'resultCircleNode' || n.type === 'draggableResultCircleNode',
        )?.data as any
      )?.objectiveDisplayMode;

      const prevLayoutKey = (
        prevNodes.find(
          (n) => n.type === 'resultCircleNode' || n.type === 'draggableResultCircleNode',
        )?.data as any
      )?.layoutKey;

      if (
        prevNodes.length === 0 ||
        prevObjectiveDisplayMode !== objectiveDisplayMode ||
        prevLayoutKey !== layoutKey
      ) {
        console.log(
          `[Stacked] Force reset triggered: prevMode=${prevObjectiveDisplayMode}, currentMode=${objectiveDisplayMode}, prevLayout=${prevLayoutKey !== layoutKey ? 'changed' : 'same'}`,
        );
        return allNodes.map((node) => {
          if (node.type === 'resultCircleNode' || node.type === 'draggableResultCircleNode') {
            return {
              ...node,
              data: {
                ...node.data,
                objectiveDisplayMode, // Store for comparison on next render
                layoutKey, // Store layout signature for comparison on next render
              },
            };
          }
          return node;
        });
      }

      // Otherwise preserve positions for smooth transitions
      const prevNodeMap = new Map(prevNodes.map((n) => [n.id, n]));
      return allNodes.map((node) => {
        const prev = prevNodeMap.get(node.id);

        // For MapNodes, always use new position (they shouldn't move)
        if (node.type === 'mapNode') {
          return node;
        }

        // For result nodes, check if collapse state has changed
        if (
          prev &&
          (node.type === 'resultCircleNode' || node.type === 'draggableResultCircleNode')
        ) {
          // Find the map this node belongs to
          const nodeObjectiveUuid = (
            node.data as ResultCircleNodeData | DraggableResultCircleNodeData
          ).objectiveUuid;
          const nodeMap = relativesMapsData.find((m) =>
            m.objectives.some((obj) => obj.uuid === nodeObjectiveUuid),
          );

          // Find the previous map state if available
          const prevMapData = prevNodeMap.get(`map-${nodeMap?.milestoneMap.uuid}`);
          const prevCollapseLevel = prevMapData?.data?.collapseLevel;
          const currentCollapseLevel = nodeMap?.collapseLevel;

          // If collapse state changed, use new position; otherwise preserve old position
          if (prevCollapseLevel !== currentCollapseLevel) {
            return node; // Use new position when collapse state changes
          }

          return { ...node, position: prev.position }; // Preserve position when collapse state unchanged
        }

        return node; // New node, use new position
      });
    });
    // CAUTION: Be aware of IDE warning here. Don't trigger
    // rerenders on every drag and drop, only when relativesMapsData
    // changes or map height changes.
    // UPDATE: Including dragAndConnectModeResultUuid, handleEnterDragAndConnectMode, handleExitDragAndConnectMode,
    // and mainMilestoneMapUuid in the dependency array to trigger re-render
    // when drag and connect mode changes (toggling between ResultCircleNode and DraggableResultCircleNode).
    // Keep an eye on this in case re-renders go crazy after. -BH 2025-07-11
  }, [
    relativesMapsData,
    updateMapState,
    dragAndConnectModeResultUuid,
    handleEnterDragAndConnectMode,
    handleExitDragAndConnectMode,
    mainMilestoneMapUuid,
    enableResultCircleFill,
    showStatusCodes,
    objectiveDisplayMode, // ✅ Fix: Trigger node recalculation when display mode changes
    layoutKey, // ✅ Fix: Trigger node recalculation when layout structure changes
  ]);

  // Use the shared node change handler
  const onNodesChange = useNodesChangeHandler(
    relativesMapsData,
    setNodes,
    'stacked',
    queueResultDeadlineUpdate, // This will now queue updates for throttled dispatch
  );

  // Update edges when nodes or dependency records change
  useEffect(() => {
    setEdges(createEdgesFromDependencies(nodes, selectedEdges, dependencyRecords));
  }, [nodes, selectedEdges, dependencyRecords]);

  // Handle keyboard events
  const onKeyDown = useKeyboardHandler(
    selectedEdges,
    removeDependenciesByIds,
    setSelectedEdges,
    dragAndConnectModeResultUuid,
    handleExitDragAndConnectMode,
  );

  // Add onConnect handler for creating new dependencies
  const onConnect = useConnectHandler(nodes, dependencyRecords, addDependency);

  // Remove dependency on edge delete (selection + delete key)
  const onEdgesChange = useEdgesChangeHandler(edges, setSelectedEdges, removeDependenciesByIds);

  // Defensive guard: return null if mainMap not found (after all hooks)
  // This can happen during initial render race condition
  if (!mainMap) {
    console.warn(
      `[Map3RelativesStacked] Main map not found. uuid: ${mainMilestoneMapUuid}, available maps: ${relatedMilestoneMaps.length}`,
    );
    return null;
  }

  return (
    <ReactFlowProvider>
      <div
        ref={flowWrapper}
        className="relative w-full"
        style={{
          width: getTotalGridWidth(
            relativesMapsData.map((m) => ({ ...m, intervals: m.defaultIntervals })),
            'stacked',
          ),
          height: getTotalGridHeight(relativesMapsData),
          background: '#f4f4f5',
        }}
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        {children}

        {dimensions.width > 0 && dimensions.height > 0 && (
          <div
            style={{
              width: dimensions.width,
              height: dimensions.height,
              position: 'relative',
              zIndex: GRID_CONFIG.zIndex.grid,
            }}
          >
            {/* 
              RENDERING OUTSIDE REACT FLOW STRATEGY
              ====================================
              
              This section renders thick lines and wrappers as regular React elements
              positioned absolutely OUTSIDE the React Flow canvas, rather than as
              React Flow nodes inside the canvas.
              
              See detailed architectural comments in the helper components above.
            */}
            <FullyCollapsedMapsGroup
              fullyCollapsedMaps={relativesMapsData.filter(
                (relative) => relative.collapseLevel === 'fullyCollapsed',
              )}
              hoveredThickLineUuid={hoveredThickLineUuid}
              setHoveredThickLineUuid={setHoveredThickLineUuid}
              updateMapState={updateMapState}
              variant="stacked"
            />

            <ReactFlow
              key={`flow-stacked-${objectiveDisplayMode || 'default'}`}
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              nodesDraggable={true}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              connectionMode={ConnectionMode.Loose}
              fitView={false}
              minZoom={1}
              maxZoom={1}
              preventScrolling={false}
              zoomOnScroll={false}
              panOnScroll={false}
              elementsSelectable={true}
              nodesFocusable={false}
              edgesFocusable={true}
              disableKeyboardA11y={false}
              panOnDrag={false}
              snapToGrid={false}
              snapGrid={[GRID_CONFIG.cellWidth, GRID_CONFIG.cellHeight]}
              className="react-flow-no-edge-animations"
              style={
                {
                  background: 'white',
                  '--xy-edge-stroke-default': '#374151',
                  '--xy-edge-stroke-width-default': '2px',
                } as React.CSSProperties
              }
              translateExtent={[
                [0, 0],
                [dimensions.width, dimensions.height],
              ]}
            >
              {/* MapNodes now handle the visual representation, no need for separate overlays */}

              {/* Master grid using ReactFlow's Background component */}
              <Background
                gap={[GRID_CONFIG.cellWidth, GRID_CONFIG.cellHeight]}
                offset={[
                  GRID_CONFIG.objectiveWidth - GRID_CONFIG.cellWidth / 2,
                  GRID_CONFIG.headerHeight - GRID_CONFIG.cellHeight / 2,
                ]}
                color={GRID_CONFIG.colors.border}
                variant={BackgroundVariant.Lines}
                style={{ zIndex: 0 }}
              />
            </ReactFlow>
          </div>
        )}
      </div>
    </ReactFlowProvider>
  );
}
